import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { wikiApi } from '../../api/wiki.js';

/**
 * /wiki/:spaceKey/:pageSlug/edit — TipTap-based editor with autosave.
 *
 * Behaviour:
 *  - On mount, loads the page content (TipTap JSON).
 *  - 2s debounced autosave on content change → status="Saving…" → "Saved".
 *  - Manual "Publish" forces a new version (passes `publish: true` to the API).
 *  - Toolbar with Bold / Italic / Strike / Code / H1 H2 H3 / Lists /
 *    Blockquote / Code block / Undo / Redo.
 *  - Slash menu (`/`) is a deferred enhancement — see DEVELOPMENT.md.
 *  - Inline-comment anchoring is a deferred enhancement.
 */

export default function PageEditor() {
  const { spaceKey, pageSlug } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [emoji, setEmoji] = useState('');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  const lastSavedRef = useRef('');
  const lastTitleRef = useRef('');
  const debounceRef = useRef(null);

  // Load
  useEffect(() => {
    wikiApi.getPageBySlug(spaceKey, pageSlug)
      .then(({ page }) => {
        setPage(page);
        setTitle(page.title);
        setEmoji(page.emoji_icon || '');
        lastSavedRef.current = page.content || '';
        lastTitleRef.current = page.title;
      })
      .catch((err) => setError(err.response?.data?.message || 'Page not found'));
  }, [spaceKey, pageSlug]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Start writing — use the toolbar above for headings, lists, and code blocks.' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor wiki-editor',
        style: 'min-height: 500px; padding: 16px; outline: none; border: 1px solid #e2e8f0; border-radius: 6px;',
      },
    },
    onUpdate: ({ editor }) => {
      scheduleSave(editor.getJSON());
    },
  });

  // Apply loaded content into the editor.
  useEffect(() => {
    if (!editor || !page) return;
    let parsed;
    try { parsed = page.content ? JSON.parse(page.content) : null; }
    catch { parsed = null; }
    if (parsed) editor.commands.setContent(parsed, false);
  }, [editor, page]);

  function scheduleSave(_jsonOrTitle) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(), 2000);
  }

  async function save({ publish = false, editSummary = null } = {}) {
    if (!page || !editor) return;
    const json = JSON.stringify(editor.getJSON());
    const titleChanged = title !== lastTitleRef.current;
    const contentChanged = json !== lastSavedRef.current;
    if (!titleChanged && !contentChanged && !publish) return;

    setStatus('saving');
    try {
      await wikiApi.updatePage(page.id, {
        title,
        emoji_icon: emoji || null,
        content: contentChanged ? json : undefined,
        status: publish ? 'published' : undefined,
        publish,
        edit_summary: editSummary || undefined,
      });
      lastSavedRef.current = json;
      lastTitleRef.current = title;
      setStatus('saved');
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
      setStatus('error');
    }
  }

  async function onPublish() {
    const summary = window.prompt('What changed? (optional)', '');
    await save({ publish: true, editSummary: summary });
    navigate(`/wiki/${spaceKey}/${pageSlug}`);
  }

  if (error && !page) return <div className="alert error" style={{ margin: 24 }} role="alert">{error}</div>;
  if (!page) return <div className="muted" style={{ padding: 24 }}>Loading editor…</div>;

  return (
    <article data-testid="wiki-page-editor" data-loaded="true" style={{ padding: '20px 32px', maxWidth: 920, margin: '0 auto' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          value={emoji}
          onChange={(e) => { setEmoji(e.target.value); scheduleSave(); }}
          maxLength={4}
          aria-label="Page emoji"
          style={{ width: 48, fontSize: 22, textAlign: 'center' }}
        />
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); scheduleSave(); }}
          aria-label="Page title"
          data-testid="page-title-input"
          style={{ flex: 1, fontSize: 26, fontWeight: 700, border: 'none', outline: 'none' }}
        />
        <span data-testid="save-status" aria-live="polite" style={{ fontSize: 12, color: status === 'error' ? '#dc2626' : '#64748b', minWidth: 60, textAlign: 'right' }}>
          {status === 'saving' && 'Saving…'}
          {status === 'saved' && 'Saved'}
          {status === 'error' && 'Save failed'}
        </span>
        <Link to={`/wiki/${spaceKey}/${pageSlug}`} className="btn ghost">Cancel</Link>
        <button type="button" className="btn primary" data-testid="publish-btn" onClick={onPublish}>Publish</button>
      </header>

      {error && <div className="alert error" role="alert">{error}</div>}

      {editor && <Toolbar editor={editor} />}
      {editor && <EditorContent editor={editor} />}
    </article>
  );
}

function Toolbar({ editor }) {
  const btn = (active, label, onClick, title) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={'btn ghost small ' + (active ? 'is-active' : '')}
      style={{ background: active ? '#e2e8f0' : 'transparent', padding: '4px 10px', fontSize: 12 }}
      title={title}
      aria-label={title}
    >
      {label}
    </button>
  );
  return (
    <div role="toolbar" aria-label="Editor toolbar"
         style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6, padding: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}
         data-testid="editor-toolbar">
      {btn(editor.isActive('bold'), <b>B</b>, () => editor.chain().focus().toggleBold().run(), 'Bold')}
      {btn(editor.isActive('italic'), <i>I</i>, () => editor.chain().focus().toggleItalic().run(), 'Italic')}
      {btn(editor.isActive('strike'), <s>S</s>, () => editor.chain().focus().toggleStrike().run(), 'Strikethrough')}
      {btn(editor.isActive('code'), <code>{'<>'}</code>, () => editor.chain().focus().toggleCode().run(), 'Inline code')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(editor.isActive('heading', { level: 1 }), 'H1', () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'Heading 1')}
      {btn(editor.isActive('heading', { level: 2 }), 'H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), 'H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'Heading 3')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(editor.isActive('bulletList'), '• List', () => editor.chain().focus().toggleBulletList().run(), 'Bullet list')}
      {btn(editor.isActive('orderedList'), '1. List', () => editor.chain().focus().toggleOrderedList().run(), 'Ordered list')}
      {btn(editor.isActive('blockquote'), '❝', () => editor.chain().focus().toggleBlockquote().run(), 'Blockquote')}
      {btn(editor.isActive('codeBlock'), '{ }', () => editor.chain().focus().toggleCodeBlock().run(), 'Code block')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(false, '↶', () => editor.chain().focus().undo().run(), 'Undo')}
      {btn(false, '↷', () => editor.chain().focus().redo().run(), 'Redo')}
    </div>
  );
}
